import React, { useState, useEffect, useRef } from "react";
import { infoCircle,advanceSettingsDivider } from "../../assets";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import { setadvanceSettings, getadvanceSettings } from "../../network/cipAPI";
import { storeApp } from "../../Store/TemplateStore/template.action";
import { WPRangedInput } from "../../packages/WPInput/WPRangedInput";
import ToggleSwitch from "../../common/styles/components/switches/CustomSwitch";
import CustomModal from "../../packages/CustomModal";
import DupontCheckbox from "../../components/DupontCheckbox";
import createNotification, {
  SUCCESS_NOTIFICATION,
  ERROR_NOTIFICATION,
} from "../../components/Notifications";

import { AdvanceSettingsConfig } from "./advancedSettings.config";
import CircularLoader from "../../common/styles/components/circularLoader";

import "./index.scss";

const DEFAULT_PRESSURE_DROP_ABS_THRESHOLD_BAR = 1;

const AdvanceSettings = ({
  isSettingsDialogOpen,
  handleSettingsDialogClose,
  selectedTrain,
  outlierRemoval = false,
  cipAndShutdownIdentification = false,
  cleaningEffectivenessCalculation = false,
  cipPredictionAndRecommendation = false,
  lifetime = false,
  onSave = null,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const appState = useSelector((state) => state.app);
  const originalAppStateRef = useRef();
  const pressure = useSelector((state) => state.pressure);
  const settingsConfigData = AdvanceSettingsConfig(t, pressure);

  const defaultSettings = useSelector((state) => {
    const isPsi = state.pressure === "psig";
    const storedValue = state.pressureDropThresholdAbsolute;

    // For bar pressure, use default value, otherwise show direct value
    const  pressure_drop_abs_threshold = isPsi
      ? storedValue
      : DEFAULT_PRESSURE_DROP_ABS_THRESHOLD_BAR;

    return {
      n_neighbors: state.clusterPoints,
      n: state.maxRelativeDistance,
      window: state.medianCurvePoints,
      pf_threshold: state.permeateFlowDropThreshold,
      pressure_drop_threshold: state.pressureDropThreshold,
      pressure_drop_abs_threshold: pressure_drop_abs_threshold,
      salt_passage_threshold: state.saltPassageIncreaseThreshold,
      min_day_between_cips:state.minDaysBetweenCIPs,
      max_days_between_cips: state.maxDaysBetweenCIPs,
      k: state.cipIdentificationResolution,
      shutdown_length: state.minIdleDaysShutdown,
      use_salt_passage: state.useSaltPassageChange,
      min_time: state.minStabilizationDaysAfterCIP,
      max_time: state.maxStabilizationDaysAfterCIP,
      min_num_points: state.minPointsForCleaningEffectiveness,
      top_exp: state.foulingAggressivenessFactor,
      adapt_pf_trend: state.adaptPermeateFlowDropThreshold,
      adapt_pressure_drop_trend: state.adaptPressureDropThreshold,
      adapt_salt_passage_trend: state.adaptSaltPassageThreshold,
      max_evals: state.numberOfIterationsToFindOptima,
      simulation_improve_accuracy: state.simulationImproveAccuracy,
      stabilization_days: state.deafultStabilizationDaysAfterCIP,
      failure_probability_threshold: state.failureProbabilityThreshold,
    };
  });

  const [isSaveDisabled, setIsSaveDisabled] = useState(true); 
  const [errors, setErrors] = useState({});
  const [showOverlayLoader, setShowOverlayLoader] = useState(false);
  const [settings, setSettings] = useState(defaultSettings);
  const [suggestedSettings, setSuggestedSettings] = useState({});
  const [initialSettings, setInitialSettings] = useState({}); // Track initial values

  const min_time = parseFloat(settings.min_time);
  const max_time = parseFloat(settings.max_time);

    // Helper function to check if current settings differ from initial settings
  const checkForChanges = (currentSettings, initialSettings) => {
    if (!initialSettings || Object.keys(initialSettings).length === 0) {
      return false;
    }
    
    for (const key in currentSettings) {
      if (currentSettings[key] !== initialSettings[key]) {
        return true;
      }
    }
    return false;
  };

  // Track if pf_threshold was present in the initial response
  const [predPfThresholdInResponse, setPredPfThresholdInResponse] =
    useState(false);

  // function to ensure original state is always restored when dialog closes
  const closeDialogWithStateRestore = () => {
    if (originalAppStateRef.current !== undefined) {
      dispatch(storeApp(originalAppStateRef.current));
    }
    handleSettingsDialogClose();
  };

  useEffect(() => {
    if (isSettingsDialogOpen) {
      // Save the original app state when dialog opens
      originalAppStateRef.current = appState;
      dispatch(storeApp(null));
      
      const getAdvancedSettings = async () => {
        setShowOverlayLoader(true);
        try {
          const response = await getadvanceSettings(selectedTrain);
          if (response && response.detail) {
            createNotification(ERROR_NOTIFICATION, "Something went wrong, please try again later.", "");
            setShowOverlayLoader(false);
            closeDialogWithStateRestore();
            return;
          }
        if (response && response.data) {
          // Handle new API structure: { settings: {...}, suggested_settings: {...} }
          const data = response.data;
          const settingsData = data.settings || {};
          const suggestedData = data.suggested_settings || {};
          // Transform settingsData for main input boxes
          const pressure_drop_threshold_flag =
            settingsData.pressure_drop_threshold_flag;
          const transformedSettings = {
            n_neighbors: settingsData.n_neighbors,
            n: settingsData.n,
            window: settingsData.window,
            pf_threshold: settingsData.pf_threshold,
            pressure_drop_threshold: settingsData.pressure_drop_threshold,
            pressure_drop_abs_threshold:
              settingsData.pressure_drop_abs_threshold,
            pressure_drop_thresholdCheckbox1:
              pressure_drop_threshold_flag === 0 ||
              pressure_drop_threshold_flag === 2,
            pressure_drop_thresholdCheckbox2:
              pressure_drop_threshold_flag === 1 ||
              pressure_drop_threshold_flag === 2,
            pressure_drop_thresholdInput1: settingsData.pressure_drop_threshold,
            pressure_drop_thresholdInput2:
              settingsData.pressure_drop_abs_threshold,
            salt_passage_threshold: settingsData.salt_passage_threshold,
            min_day_between_cips: settingsData.min_day_between_cips,
            max_days_between_cips: settingsData.max_days_between_cips,
            k: settingsData.k,
            shutdown_length: settingsData.shutdown_length,
            use_salt_passage:
              settingsData.use_salt_passage === "1" ||
              settingsData.use_salt_passage === true,
            min_time: settingsData.min_time,
            max_time: settingsData.max_time,
            min_num_points: settingsData.min_num_points,
            top_exp: settingsData.top_exp,
            adapt_pf_trend:
              settingsData.adapt_pf_trend === "1" ||
              settingsData.adapt_pf_trend === true,
            adapt_pressure_drop_trend:
              settingsData.adapt_pressure_drop_trend === "1" ||
              settingsData.adapt_pressure_drop_trend === true,
            adapt_salt_passage_trend:
              settingsData.adapt_salt_passage_trend === "1" ||
              settingsData.adapt_salt_passage_trend === true,
            max_evals: settingsData.max_evals,
            stabilization_days: settingsData.stabilization_days,
            simulation_improve_accuracy:
              settingsData.simulation_improve_accuracy === "1" ||
              settingsData.simulation_improve_accuracy === true,
            failure_probability_threshold:
              settingsData.failure_probability_threshold,
          };
          if (
            Object.prototype.hasOwnProperty.call(
              settingsData,
              "pf_threshold"
            )
          ) {
            transformedSettings.pf_threshold =
              settingsData.pf_threshold;
            setPredPfThresholdInResponse(true);
          } else {
            setPredPfThresholdInResponse(false);
          }

          // Transform suggestedData for disabled input boxes
          const suggestedSettingsTransformed = { ...suggestedData };

          setSettings(transformedSettings);
          setInitialSettings(transformedSettings); // Store initial settings for comparison
          setSuggestedSettings(suggestedSettingsTransformed);
        }
      } catch (error) {
        setShowOverlayLoader(false);
      }
      finally{
        setShowOverlayLoader(false);
      }
      };
      
      getAdvancedSettings();
    }
  }, [isSettingsDialogOpen, selectedTrain]);

  const handleChange = (settingKey, value, minValue, maxValue) => {
    let errorMessage = null;
    if (settingKey !== "stabilization_days") {
      if (
        value === "" ||
        parseFloat(value) < minValue ||
        parseFloat(value) > maxValue
      ) {
        errorMessage = `${t("advanceSettings.Rangemsg")} ${minValue} ${t(
          "advanceSettings.And"
        )} ${maxValue}`;
      }
    } else {
      // If it's the "stabilization_days" field
      const minTimeValue = parseFloat(settings.min_time);
      const maxTimeValue = parseFloat(settings.max_time);
      if (
        value === "" ||
        parseFloat(value) < minTimeValue ||
        parseFloat(value) > maxTimeValue
      ) {
        errorMessage = `${t("advanceSettings.Rangemsg")} ${minTimeValue} ${t(
          "advanceSettings.And"
        )} ${maxTimeValue}`;
      }
    }
    const hasErrors = Object.values({
      ...errors,
      [settingKey]: errorMessage,
    }).some((error) => error !== null);

    setErrors({ ...errors, [settingKey]: errorMessage });
    const updatedSettings = { ...settings, [settingKey]: value };
    setSettings(updatedSettings);
    setIsSaveDisabled(hasErrors || !hasCheckedPressureDropThreshold);
  };

  // Always set boolean value for toggle
  const handleRadioChange = (settingKey, value) => {
    setSettings({
      ...settings,
      [settingKey]: Boolean(value),
    });
  };

  // Check if at least one checkbox is checked in pressure drop threshold group
  const hasCheckedPressureDropThreshold = React.useMemo(() => {
    const checkbox1 = settings.pressure_drop_thresholdCheckbox1;
    const checkbox2 = settings.pressure_drop_thresholdCheckbox2;
    const { pressure_drop_threshold } =
      settingsConfigData.cipAndShutdownIdentification;
    return (
      (checkbox1 === undefined &&
        "percentage" === pressure_drop_threshold.defaultSelected) ||
      (checkbox2 === undefined &&
        "absolute" === pressure_drop_threshold.defaultSelected) ||
      !!checkbox1 ||
      !!checkbox2
    );
  }, [settings, settingsConfigData]);

  // Check for changes and update save button state
  useEffect(() => {
    const hasSettingsChanged = checkForChanges(settings, initialSettings);
    const hasValidationErrors = Object.values(errors).some((error) => error !== null);
    setIsSaveDisabled(!hasSettingsChanged || hasValidationErrors || !hasCheckedPressureDropThreshold);
  }, [settings, initialSettings, errors, hasCheckedPressureDropThreshold]);

  const preparePayload = () => {
    // Determine which pressure drop threshold values to send and the flag value
    const percentChecked =
      settings.pressure_drop_thresholdCheckbox1 === undefined
        ? "percentage" ===
          settingsConfigData.cipAndShutdownIdentification
            .pressure_drop_threshold.defaultSelected
        : !!settings.pressure_drop_thresholdCheckbox1;

    const absoluteChecked =
      settings.pressure_drop_thresholdCheckbox2 === undefined
        ? "absolute" ===
          settingsConfigData.cipAndShutdownIdentification
            .pressure_drop_threshold.defaultSelected
        : !!settings.pressure_drop_thresholdCheckbox2;

    // Create an optimized pressure drop threshold payload
    const pressure_drop_threshold_flag =
      percentChecked && absoluteChecked
        ? 2
        : absoluteChecked
        ? 1
        : percentChecked
        ? 0
        : undefined;

    // Prepare the pressure drop values with null safety
    const pressureDropValues = {
      ...(percentChecked && {
        pressure_drop_threshold:
          settings.pressure_drop_thresholdInput1 !== undefined
            ? settings.pressure_drop_thresholdInput1
            : settingsConfigData.cipAndShutdownIdentification
                .pressure_drop_threshold.options.percentage.defaultValue,
      }),
      ...(absoluteChecked && {
        pressure_drop_abs_threshold:
          settings.pressure_drop_thresholdInput2 !== undefined
            ? settings.pressure_drop_thresholdInput2
            : settingsConfigData.cipAndShutdownIdentification
                .pressure_drop_threshold.options.absolute.defaultValue,
      }),
    };

    // Always add pf_threshold, salt_passage_threshold, and pressure_drop_threshold keys for adapt_pressure_drop_trend to payload (no  prefix)
    const cipPredictionPayload = {
      pf_threshold:
        settings.pf_threshold !== undefined
          ? settings.pf_threshold
          : 15,
      salt_passage_threshold:
        settings.salt_passage_threshold !== undefined
          ? settings.salt_passage_threshold
          : settingsConfigData?.cipPredictionAndRecommendation
              ?.salt_passage_threshold?.defaultValue ?? 0,
    };

    // Add all input values for adapt_pressure_drop_trend and pressure_drop_threshold_flag (no  prefix)
    const groupConfig = Object.entries(
      settingsConfigData.cipAndShutdownIdentification
    ).find(([k, v]) => v.type === "checkboxGroup");
    if (groupConfig) {
      const groupKey = groupConfig[0];
      // Calculate pressure_drop_threshold_flag as in cip identification
      const checkbox1 = settings[`${groupKey}Checkbox1`];
      const checkbox2 = settings[`${groupKey}Checkbox2`];
      const defaultSelected = groupConfig[1].defaultSelected;
      const percentChecked =
        checkbox1 === undefined
          ? defaultSelected === "percentage"
          : !!checkbox1;
      const absoluteChecked =
        checkbox2 === undefined
          ? defaultSelected === "absolute"
          : !!checkbox2;
      let flag;
      if (percentChecked && absoluteChecked) flag = 2;
      else if (absoluteChecked) flag = 1;
      else if (percentChecked) flag = 0;
      cipPredictionPayload[`${groupKey}_flag`] = flag;
      // Always send pressure_drop_threshold and pressure_drop_abs_threshold
      cipPredictionPayload["pressure_drop_threshold"] =
        settings.pressure_drop_threshold !== undefined
          ? settings.pressure_drop_threshold
          : settings[`${groupKey}Input1`];
      cipPredictionPayload["pressure_drop_abs_threshold"] =
        settings.pressure_drop_abs_threshold !== undefined
          ? settings.pressure_drop_abs_threshold
          : settings[`${groupKey}Input2`];
      // Remove any pressure_drop_thresholdInput1/Input2 from payload if present
      delete cipPredictionPayload["pressure_drop_thresholdInput1"];
      delete cipPredictionPayload["pressure_drop_thresholdInput2"];
    }

    const payload = {
      n_neighbors: settings.n_neighbors,
      window: settings.window,
      n: settings.n,
      // Include all checked pressure drop values
      ...pressureDropValues,
      pressure_drop_threshold_flag, // Include the flag
      salt_passage_threshold: settings.salt_passage_threshold,
      min_day_between_cips: settings.min_day_between_cips,
      max_days_between_cips: settings.max_days_between_cips,
      k: settings.k,
      shutdown_length: settings.shutdown_length,
      use_salt_passage: settings.use_salt_passage,
      min_time: settings.min_time,
      max_time: settings.max_time,
      min_num_points: settings.min_num_points,
      top_exp: settings.top_exp,
      adapt_pf_trend: settings.adapt_pf_trend,
      adapt_pressure_drop_trend: settings.adapt_pressure_drop_trend,
      adapt_salt_passage_trend: settings.adapt_salt_passage_trend,
      max_evals: settings.max_evals,
      stabilization_days: settings.stabilization_days,
      simulation_improve_accuracy: settings.simulation_improve_accuracy,
      failure_probability_threshold: settings.failure_probability_threshold,
      ...cipPredictionPayload,
    };

    return payload;
  };

  const handleSaveSettings = async () => {
    setShowOverlayLoader(true);
    try {
      const payload = preparePayload();
      const response = await setadvanceSettings(payload, selectedTrain);
      // Check for specific error response
      if (response && response.detail) {
        createNotification(ERROR_NOTIFICATION, "Something went wrong, please try again later.", "");
        closeDialogWithStateRestore();
        return;
      }
      if (response) {
        setSettings(response);
        createNotification(
          SUCCESS_NOTIFICATION,
          t("advanceSettings.savemsg"),
          ""
        );
        // Call the onSave callback if provided
        if (onSave && typeof onSave === 'function') {
          onSave();
        }
      } else {
        createNotification(ERROR_NOTIFICATION, response.detail?.error || "Something went wrong, please try again later.", "");
      }
    } catch (error) {
      createNotification(ERROR_NOTIFICATION, error.message || t("advanceSettings.somethingWentWrong"), "");
    } finally {
      setShowOverlayLoader(false);
      closeDialogWithStateRestore();
    }
  };

  const handleRestoreDefaults = () => {
    setSettings(defaultSettings);
    setErrors({});
    // Check if default settings have at least one checkbox checked
    const hasDefaultChecked =
      settingsConfigData.cipAndShutdownIdentification.pressure_drop_threshold
        .defaultSelected !== undefined;
    setIsSaveDisabled(!hasDefaultChecked);
  };

  const onClose = ({ target }) => {
    if (target.id === "modal-confirm-btn") {
      handleSaveSettings();
    } else if (
      target.id === "modal-cancel-btn" ||
      target.id === "modal-close-icon"
    ) {
      closeDialogWithStateRestore();
    } else if (target.id === "modal-restore-btn") {
      handleRestoreDefaults();
    }
  };

  return (
    <>
      <CustomModal
        show={isSettingsDialogOpen}
        onModalClose={onClose}
        header={t("analyseData.settings")}
        cancelBtn={t("ValueStrings.cancel")}
        confirmBtn={t("ValueStrings.save")}
        restoreDefaultsBtn={t("advanceSettings.restoredefaults")}
        confirmBtnDisabled={isSaveDisabled}
        width="810px"
        isLoading={showOverlayLoader}
        custombodyclass={"modal-body-container-advance"}
      >
        <div className="advance-setting-popup descriptionText font-14 ">
          <div className="padding10 position-relative  ">
            <div className="advance-settings-note">
              <span className="advance-settings-note__icon">
                <img src={infoCircle} alt="info" />
              </span>
              <span className="advance-settings-note__text">
                {t("advanceSettings.note")}
              </span>
            </div>
            {/* Common legend for input boxes */}
            <div className="advance-settings-legend">
              <div className="advance-settings-legend-row">
                <span className="advance-settings-legend-current">{t("advanceSettings.current")}</span>
                {suggestedSettings && Object.keys(suggestedSettings).length > 0 && (
                  <span className="advance-settings-legend-suggested">{t("advanceSettings.suggested")}</span>
                )}
              </div>
            </div>
            {outlierRemoval && (
              <div className=" marginTop10 marginBtm10">
                <div className="subheading__title">
                  {t("advanceSettings.outlierRemoval")}
                </div>
                {Object.keys(settingsConfigData.outlierRemoval).map(
                  (settingKey) => (
                    <div className="flex-between-center" key={settingKey}>
                      <label className="font-14">
                        {settingsConfigData.outlierRemoval[settingKey].label}
                      </label>
                      <div
                        className={`dropdown-width  flex-gap-12 marginBtm10${((Object.keys(suggestedSettings).length === 0) ? ' dropdown-width-single' : '')} flex-gap-12`}
                      >
                        <WPRangedInput
                          value={
                            settings[settingKey] === undefined
                              ? defaultSettings[settingKey]
                              : settings[settingKey]
                          }
                          onChange={e =>
                            handleChange(
                              settingKey,
                              e && e.target ? e.target.value : e,
                              settingsConfigData.outlierRemoval[settingKey].ranges?.hard?.min,
                              settingsConfigData.outlierRemoval[settingKey].ranges?.hard?.max
                            )
                          }
                          ranges={settingsConfigData.outlierRemoval[settingKey].ranges}
                          errorMsg={errors[settingKey]}
                          isError={!!errors[settingKey]}
                          maxLength={10}
                          integer={settingsConfigData.outlierRemoval[settingKey].integer}
                          style={{ width: (suggestedSettings[settingKey] !== null && suggestedSettings[settingKey] !== undefined) ? 124 : 124 }}
                        />
                        {suggestedSettings[settingKey] !== null &&
                          suggestedSettings[settingKey] !== undefined && (
                            <WPRangedInput
                              value={suggestedSettings[settingKey]}
                              disabled={true}
                              ranges={settingsConfigData.outlierRemoval[settingKey].ranges}
                              maxLength={10}
                              integer={settingsConfigData.outlierRemoval[settingKey].integer}
                              style={{ width: 124 }}
                            />
                          )}
                      </div>
                    </div>
                  )
                )}
                <img src={advanceSettingsDivider} alt="divider"  className="advance-settings-divider" />
              </div>
            )}
            {cipAndShutdownIdentification && (
              <div className="marginBtm10">
                <div className="subheading__title">
                  {t("advanceSettings.cipAndShutdownIdentification")}
                </div>
                {Object.entries(
                  settingsConfigData.cipAndShutdownIdentification
                ).map(([settingKey, settingConfig]) => {
                  if (settingConfig.type === "checkboxGroup") {
                    return (
                      <div
                        key={settingKey}
                        className="advance-settings-checkbox-group"
                      >
                        <label className="font-14 pressure-drop-label">
                          {settingConfig.label}
                        </label>
                        <div className="pressure-drop-section">
                          {Object.entries(settingConfig.options).map(
                            ([optionKey, optionConfig], index) => {
                              const checkboxId = `${settingKey}Checkbox${index + 1}`;
                              const inputKey = `${settingKey}Input${index + 1}`;
                              return (
                                <div
                                  className="marginBtm8 flex-between-center"
                                  key={optionKey}
                                >
                                  <div className="pressure-drop-checkbox-row">
                                    <DupontCheckbox
                                      id={checkboxId}
                                      labelText={optionConfig.label}
                                      checked={
                                        settings[checkboxId] === undefined
                                          ? optionKey === settingConfig.defaultSelected
                                          : !!settings[checkboxId]
                                      }
                                      onChange={(checked) => {
                                        if (!checked && errors[inputKey]) {
                                          return;
                                        }
                                        const updatedSettings = {
                                          ...settings,
                                          [checkboxId]: checked,
                                        };
                                        setSettings(updatedSettings);
                                        const willHaveCheckedBox =
                                          (optionKey === "percentage"
                                            ? checked
                                            : !!settings.pressure_drop_thresholdCheckbox1) ||
                                          (optionKey === "absolute"
                                            ? checked
                                            : !!settings.pressure_drop_thresholdCheckbox2);
                                        setIsSaveDisabled(
                                          !willHaveCheckedBox ||
                                            Object.values(errors).some((error) => error !== null)
                                        );
                                      }}
                                      disabled={settings[checkboxId] !== false && errors[inputKey]}
                                    />
                                  </div>
                                  <div
                                    className={`dropdown-width flex-gap-12 ${(Object.keys(suggestedSettings).length === 0 ? ' dropdown-width-single' : '')}`}
                                  
                                  >
                                    <WPRangedInput
                                      value={
                                        settings[inputKey] === undefined
                                          ? optionConfig.defaultValue
                                          : settings[inputKey]
                                      }
                                      onChange={e => {
                                        const value = e && e.target ? e.target.value : e;
                                        handleChange(
                                          inputKey,
                                          value,
                                          optionConfig.ranges?.hard?.min,
                                          optionConfig.ranges?.hard?.max
                                        );
                                        if (inputKey === "pressure_drop_thresholdInput1") {
                                          setSettings((prev) => ({
                                            ...prev,
                                            pressure_drop_threshold: value,
                                          }));
                                        } else if (inputKey === "pressure_drop_thresholdInput2") {
                                          setSettings((prev) => ({
                                            ...prev,
                                            pressure_drop_abs_threshold: value,
                                          }));
                                        }
                                      }}
                                      ranges={optionConfig.ranges}
                                      errorMsg={errors[inputKey]}
                                      isError={!!errors[inputKey]}
                                      maxLength={10}
                                      integer={optionConfig.integer}
                                      disabled={
                                        !(settings[checkboxId] === undefined
                                          ? optionKey === settingConfig.defaultSelected
                                          : !!settings[checkboxId])
                                      }
                                    />
                                    {/* Show suggested input only if key exists in suggestedSettings */}
                                    {inputKey === "pressure_drop_thresholdInput1" &&
                                      suggestedSettings.pressure_drop_threshold !== null &&
                                      suggestedSettings.pressure_drop_threshold !== undefined && (
                                        <WPRangedInput
                                          value={suggestedSettings.pressure_drop_threshold}
                                          disabled={true}
                                          ranges={optionConfig.ranges}
                                          maxLength={10}
                                          integer={optionConfig.integer}
                                        />
                                      )}
                                    {inputKey === "pressure_drop_thresholdInput2" &&
                                      suggestedSettings.pressure_drop_abs_threshold !== null &&
                                      suggestedSettings.pressure_drop_abs_threshold !== undefined && (
                                        <WPRangedInput
                                          value={suggestedSettings.pressure_drop_abs_threshold}
                                          disabled={true}
                                          ranges={optionConfig.ranges}
                                          maxLength={10}
                                          integer={optionConfig.integer}
                                        />
                                      )}
                                  </div>
                                </div>
                              );
                            }
                          )}
                        </div>
                      </div>
                    );
                  }

                  if (settingConfig.type === "radio") {
                    return (
                      <div className="flex-between-center" key={settingKey}>
                        <label className="font-14 margin-radio">
                          {settingConfig.label}
                        </label>
                        <div
                          className={`toggle-switch-container dropdown-width${(suggestedSettings[settingKey] === null || suggestedSettings[settingKey] === undefined) ? ' dropdown-width-single' : ''}`}
                          style={{ display: "flex", gap: 90, width: (suggestedSettings[settingKey] === null || suggestedSettings[settingKey] === undefined) ? 124 : undefined }}
                        >
                          <ToggleSwitch
                            id={`toggle_${settingKey}`}
                            name={settingKey}
                            checked={Boolean(
                              settings[settingKey] === undefined
                                ? defaultSettings[settingKey]
                                : settings[settingKey]
                            )}
                            onChange={(checked) =>
                              handleRadioChange(settingKey, checked)
                            }
                            small
                          />
                          {suggestedSettings[settingKey] !== null &&
                            suggestedSettings[settingKey] !== undefined && (
                              <ToggleSwitch
                                id={`toggle_${settingKey}_disabled`}
                                name={settingKey}
                                checked={!!suggestedSettings[settingKey]}
                                disabled={true}
                                small
                              />
                            )}
                        </div>
                      </div>
                    );
                  }

                  if (settingConfig.type === "number") {
                    return (
                      <div className="flex-between-center" key={settingKey}>
                        <label className="font-14">{settingConfig.label}</label>
                        <div
                          className={`dropdown-width flex-gap-12  marginBtm10${(suggestedSettings[settingKey] === null || suggestedSettings[settingKey] === undefined) ? ' dropdown-width-single' : ''}`}
                        
                        >
                          <WPRangedInput
                            value={
                              settings[settingKey] === undefined
                                ? defaultSettings[settingKey]
                                : settings[settingKey]
                            }
                            onChange={e =>
                              handleChange(
                                settingKey,
                                e && e.target ? e.target.value : e,
                                settingConfig.ranges?.hard?.min,
                                settingConfig.ranges?.hard?.max
                              )
                            }
                            ranges={settingConfig.ranges}
                            errorMsg={errors[settingKey]}
                            isError={!!errors[settingKey]}
                            maxLength={10}
                            integer={settingConfig.integer}
                            style={{ width: (suggestedSettings[settingKey] !== null && suggestedSettings[settingKey] !== undefined) ? 124 : 124 }}
                          />
                          {Object.prototype.hasOwnProperty.call(suggestedSettings, settingKey) && (
                            <WPRangedInput
                              value={suggestedSettings[settingKey]}
                              disabled={true}
                              ranges={settingConfig.ranges}
                              maxLength={10}
                              integer={settingConfig.integer}
                   style={{ width: 124 }}
                            />
                          )}
                        </div>
                      </div>
                    );
                  }

                  return null;
                })}
              </div>
            )}
            {cleaningEffectivenessCalculation && (
              <div className=" marginBtm10">
                <div className="subheading__title">
                  {t("advanceSettings.cleaningEffectivenessCalculation")}
                </div>
                {Object.keys(
                  settingsConfigData.cleaningEffectivenessCalculation
                ).map((settingKey) => (
                  <div className="flex-between-center" key={settingKey}>
                    <label className="font-14">
                      {
                        settingsConfigData.cleaningEffectivenessCalculation[
                          settingKey
                        ].label
                      }
                    </label>
                    <div
                      className={`dropdown-width flex-gap-12  marginBtm10 marginBtm10${(suggestedSettings[settingKey] === null || suggestedSettings[settingKey] === undefined) ? ' dropdown-width-single' : ''}`}
                 
                    >
                      <WPRangedInput
                        value={
                          settings[settingKey] === undefined
                            ? defaultSettings[settingKey]
                            : settings[settingKey]
                        }
                        onChange={e =>
                          handleChange(
                            settingKey,
                            e && e.target ? e.target.value : e,
                            settingsConfigData.cleaningEffectivenessCalculation[
                              settingKey
                            ].ranges?.hard?.min,
                            settingsConfigData.cleaningEffectivenessCalculation[
                              settingKey
                            ].ranges?.hard?.max
                          )
                        }
                        ranges={settingsConfigData.cleaningEffectivenessCalculation[settingKey].ranges}
                        errorMsg={errors[settingKey]}
                        isError={!!errors[settingKey]}
                        maxLength={10}
                        integer={settingsConfigData.cleaningEffectivenessCalculation[settingKey].integer}
                      />
                      {suggestedSettings[settingKey] !== null &&
                        suggestedSettings[settingKey] !== undefined && (
                          <WPRangedInput
                            value={suggestedSettings[settingKey]}
                            disabled={true}
                            ranges={settingsConfigData.cleaningEffectivenessCalculation[settingKey].ranges}
                            maxLength={10}
                            integer={settingsConfigData.cleaningEffectivenessCalculation[settingKey].integer}
                          />
                        )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {cipPredictionAndRecommendation && (
              <div className=" marginBtm10">
                <div className="subheading__title">
                  {t("advanceSettings.cipPredictionAndRecommendation")}
                </div>
                {Object.keys(
                  settingsConfigData.cipPredictionAndRecommendation
                ).map((settingKey) => {
                  // Special handling for adapt_pf_trend and adapt_salt_passage_trend
                  if (
                    settingKey === "adapt_pf_trend" ||
                    settingKey === "adapt_salt_passage_trend"
                  ) {
                    const isToggleOn = Boolean(
                      settings[settingKey] === undefined
                        ? defaultSettings[settingKey]
                        : settings[settingKey]
                    );
                    // Key and config for the threshold
                    const thresholdKey =
                      settingKey === "adapt_pf_trend"
                        ? "pf_threshold"
                        : "salt_passage_threshold";
                    const subLabel =
                      settingKey === "adapt_pf_trend"
                        ? "Permeate flow drop threshold for a CIP(%)"
                        : "Salt passage increase threshold for a CIP (%)";
                    // Direct values for threshold validation
                    const thresholdConfig = {
                      ranges: { hard: { min: 10, max: 100 } },
                      integer: false,
                      defaultValue: settingKey === "adapt_pf_trend" ? 15 : 20
                    };
                    const thresholdMin = 10;
                    const thresholdMax = 100;
                    const thresholdDefault = settingKey === "adapt_pf_trend" ? 15 : 20;
                    const thresholdValue =
                      settings[thresholdKey] === undefined
                        ? defaultSettings[thresholdKey] !== undefined
                          ? defaultSettings[thresholdKey]
                          : thresholdDefault
                        : settings[thresholdKey];
                    return (
                      <div
                        key={settingKey}
                        className="advance-settings-checkbox-group marginBtm10"
                      >
                        <div className="flex-between-center">
                          <label className="font-14 margin-radio">
                            {settingsConfigData.cipPredictionAndRecommendation[settingKey].label}
                          </label>
                          <div
                            className={`toggle-switch-container dropdown-width${(suggestedSettings[settingKey] === null || suggestedSettings[settingKey] === undefined) ? ' dropdown-width-single' : ''}`}
                            style={{ display: "flex", gap: 90 }}
                          >
                            <ToggleSwitch
                              id={`toggle_${settingKey}`}
                              name={settingKey}
                              checked={isToggleOn}
                              onChange={(checked) => handleRadioChange(settingKey, checked)}
                              small
                            />
                            {/* Suggested toggle always reflects suggestedSettings, not main state */}
                            {suggestedSettings[settingKey] !== null &&
                              suggestedSettings[settingKey] !== undefined && (
                                <ToggleSwitch
                                  id={`toggle_${settingKey}_disabled`}
                                  name={settingKey}
                                  checked={Boolean(suggestedSettings[settingKey])}
                                  disabled={true}
                                  small
                                />
                              )}
                          </div>
                        </div>
                        {/* Sublabel and input for threshold */}
                        <div className="marginBtm10 marginBtm10 advance-settings-threshold-row">
                          <div className="font-12 advance-settings-sub-label">
                            {t(`advanceSettings.${settingKey === "adapt_pf_trend" ? "permeateFlowDropThresholdForCIP" : "saltPassageIncreaseThresholdForCIP"}`)}
                          </div>
                          <div
                            className={`dropdown-width marginBtm10${(Object.keys(suggestedSettings).length === 0) ? ' dropdown-width-single' : ''}`}
                            style={
                              Object.keys(suggestedSettings).length === 0
                                ? { marginLeft: 290 }
                                : {
                                    display: "flex",
                                    marginLeft: 180,
                                    gap: 12,
                                    alignItems: "center",
                                  
                                  }
                            }
                          >
                            <WPRangedInput
                              value={thresholdValue}
                              onChange={e => {
                                const value = e && e.target ? e.target.value : e;
                                handleChange(
                                  thresholdKey,
                                  value,
                                  thresholdMin,
                                  thresholdMax
                                );
                              }}
                              ranges={thresholdConfig.ranges}
                              errorMsg={errors[thresholdKey]}
                              isError={!!errors[thresholdKey]}
                              maxLength={10}
                              integer={thresholdConfig.integer}
                              disabled={isToggleOn}
                            />
                            {/* Always show suggested input if present, and always from suggestedSettings. If not, reserve space for alignment. */}
                            {suggestedSettings[thresholdKey] !== null &&
                              suggestedSettings[thresholdKey] !== undefined ? (
                                <WPRangedInput
                                  value={suggestedSettings[thresholdKey]}
                                  disabled={true}
                                  ranges={thresholdConfig.ranges}
                                  maxLength={10}
                                  integer={thresholdConfig.integer}
                                />
                              ) : (
                                <div style={{ width: 130, minHeight: 1 }} />
                              )}
                          </div>
                        </div>
                      </div>
                    );
                  } else if (settingKey === "adapt_pressure_drop_trend") {
                    const isToggleOn = Boolean(
                      settings[settingKey] === undefined
                        ? defaultSettings[settingKey]
                        : settings[settingKey]
                    );
                    // Get the checkboxGroup config from cipAndShutdownIdentification
                    const groupConfig = Object.entries(
                      settingsConfigData.cipAndShutdownIdentification
                    ).find(([k, v]) => v.type === "checkboxGroup");
                    const groupOptions = groupConfig
                      ? groupConfig[1].options
                      : {};
                    const groupKey = groupConfig
                      ? groupConfig[0]
                      : "pressure_drop_threshold";
                    return (
                      <div
                        key={settingKey}
                        className="advance-settings-checkbox-group marginBtm10"
                      >
                        <div className="flex-between-center">
                          <label className="font-14 margin-radio">
                            {
                              settingsConfigData.cipPredictionAndRecommendation[
                                settingKey
                              ].label
                            }
                          </label>
                          <div
                            className={`toggle-switch-container dropdown-width${(suggestedSettings[settingKey] === null || suggestedSettings[settingKey] === undefined) ? ' dropdown-width-single' : ''}`}
                            style={{ display: "flex", gap: 90, width: (suggestedSettings[settingKey] === null || suggestedSettings[settingKey] === undefined) ? 124 : undefined }}
                          >
                            <ToggleSwitch
                              id={`toggle_${settingKey}`}
                              name={settingKey}
                              checked={isToggleOn}
                              onChange={(checked) =>
                                handleRadioChange(settingKey, checked)
                              }
                              small
                            />
                            {/* Suggested toggle always reflects suggestedSettings, not main state */}
                            {suggestedSettings[settingKey] !== null &&
                              suggestedSettings[settingKey] !== undefined && (
                                <ToggleSwitch
                                  id={`toggle_${settingKey}_disabled`}
                                  name={settingKey}
                                  checked={Boolean(
                                    suggestedSettings[settingKey]
                                  )}
                                  disabled={true}
                                  small
                                />
                              )}
                          </div>
                        </div>
                        {/* Sublabel for pressure drop threshold */}
                        <div className="font-12 advance-settings-pressure-drop-label">
                          {t("advanceSettings.pressureDropThresholdForCIP")}
                        </div>
                        <div
                          className="pressure-drop-section"
                          style={{ marginLeft: 64 }}
                        >
                          {Object.entries(groupOptions).map(
                            ([optionKey, optionConfig], index) => {
                              const checkboxId = `${groupKey}Checkbox${
                                index + 1
                              }`;
                              const inputKey = `${groupKey}Input${
                                index + 1
                              }`;
                              return (
                                <div
                                  className="marginBtm16 flex-between-center"
                                  key={optionKey}
                                  style={{ marginBottom: 10 }}
                                >
                                  <div className="pressure-drop-checkbox-row">
                                    <DupontCheckbox
                                      id={checkboxId}
                                      labelText={optionConfig.label}
                                      checked={
                                        settings[checkboxId] === undefined
                                          ? optionKey ===
                                            groupConfig[1].defaultSelected
                                          : !!settings[checkboxId]
                                      }
                                      onChange={(checked) => {
                                        if (!checked && errors[inputKey]) {
                                          return;
                                        }
                                        const updatedSettings = {
                                          ...settings,
                                          [checkboxId]: checked,
                                        };
                                        setSettings(updatedSettings);
                                      }}
                                      disabled={isToggleOn}
                                    />
                                  </div>
                                  <div
                                  className={`dropdown-width flex-gap-12  ${(Object.keys(suggestedSettings).length === 0 ? ' dropdown-width-single' : '')}`}
                                   
                                  >
                                    <WPRangedInput
                                      value={
                                        settings[inputKey] === undefined
                                          ? optionConfig.defaultValue
                                          : settings[inputKey]
                                      }
                                      onChange={e => {
                                        const value = e && e.target ? e.target.value : e;
                                        handleChange(
                                          inputKey,
                                          value,
                                          optionConfig.ranges?.hard?.min,
                                          optionConfig.ranges?.hard?.max
                                        );
                                      }}
                                      ranges={optionConfig.ranges}
                                      errorMsg={errors[inputKey]}
                                      isError={!!errors[inputKey]}
                                      maxLength={10}
                                      disabled={
                                        isToggleOn ||
                                        !(settings[checkboxId] === undefined
                                          ? optionKey ===
                                            groupConfig[1].defaultSelected
                                          : !!settings[checkboxId])
                                      }
                                    />
                                    {/* Show suggested input for correct key if present */}
                                    {((inputKey === "pressure_drop_thresholdInput1" &&
                                        suggestedSettings.pressure_drop_threshold !== null &&
                                        suggestedSettings.pressure_drop_threshold !== undefined) ||
                                      (inputKey === "pressure_drop_thresholdInput2" &&
                                        suggestedSettings.pressure_drop_abs_threshold !== null &&
                                        suggestedSettings.pressure_drop_abs_threshold !== undefined)) && (
                                      <WPRangedInput
                                        value={inputKey === "pressure_drop_thresholdInput1"
                                          ? suggestedSettings.pressure_drop_threshold
                                          : suggestedSettings.pressure_drop_abs_threshold}
                                        disabled={true}
                                        ranges={optionConfig.ranges}
                                        maxLength={10}
                                      />
                                    )}
                                  </div>
                                </div>
                              );
                            }
                          )}
                        </div>
                      </div>
                    );
                  }
                  // Default rendering for other keys
                  if (
                    settingsConfigData.cipPredictionAndRecommendation[
                      settingKey
                    ].isBoolean
                  ) {
                    return (
                      <div className="flex-between-center" key={settingKey}>
                        <label className="font-14 margin-radio">
                          {
                            settingsConfigData.cipPredictionAndRecommendation[
                              settingKey
                            ].label
                          }
                        </label>
                        <div
                          className={`toggle-switch-container dropdown-width${(suggestedSettings[settingKey] === null || suggestedSettings[settingKey] === undefined) ? ' dropdown-width-single' : ''}`}
                          style={{ display: "flex", gap: 90 }}
                        >
                          <ToggleSwitch
                            id={`toggle_${settingKey}`}
                            name={settingKey}
                            checked={Boolean(
                              settings[settingKey] === undefined
                                ? defaultSettings[settingKey]
                                : settings[settingKey]
                            )}
                            onChange={(checked) =>
                              handleRadioChange(settingKey, checked)
                            }
                            small
                          />
                          {/* Suggested toggle always reflects suggestedSettings, not main state */}
                          {suggestedSettings[settingKey] !== null &&
                            suggestedSettings[settingKey] !== undefined && (
                              <ToggleSwitch
                                id={`toggle_${settingKey}_disabled`}
                                name={settingKey}
                                checked={Boolean(suggestedSettings[settingKey])}
                                disabled={true}
                                small
                              />
                            )}
                        </div>
                      </div>
                    );
                  }
                  // Number input rendering
                  return (
                    <div className=" flex-between-center" key={settingKey}>
                      <label className="font-14">
                        {
                          settingsConfigData.cipPredictionAndRecommendation[
                            settingKey
                          ].label
                        }
                      </label>
                      <div
                        className={`dropdown-width flex-gap-12 marginBtm10${(suggestedSettings[settingKey] === null || suggestedSettings[settingKey] === undefined) ? ' dropdown-width-single' : ''}`}
                       
                      >
                        <WPRangedInput
                          value={
                            settings[settingKey] === undefined
                              ? defaultSettings[settingKey]
                              : settings[settingKey]
                          }
                          onChange={e =>
                            handleChange(
                              settingKey,
                              e && e.target ? e.target.value : e,
                              settingsConfigData.cipPredictionAndRecommendation[
                                settingKey
                              ].ranges?.hard?.min,
                              settingsConfigData.cipPredictionAndRecommendation[
                                settingKey
                              ].ranges?.hard?.max
                            )
                          }
                          ranges={settingsConfigData.cipPredictionAndRecommendation[settingKey].ranges}
                          errorMsg={errors[settingKey]}
                          isError={!!errors[settingKey]}
                          maxLength={10}
                          integer={settingsConfigData.cipPredictionAndRecommendation[settingKey].integer}
                        />
                        {/* Always show suggested input if present, and always from suggestedSettings */}
                        {suggestedSettings[settingKey] !== null &&
                          suggestedSettings[settingKey] !== undefined && (
                            <WPRangedInput
                              value={suggestedSettings[settingKey]}
                              disabled={true}
                              ranges={settingsConfigData.cipPredictionAndRecommendation[settingKey].ranges}
                              maxLength={10}
                              integer={settingsConfigData.cipPredictionAndRecommendation[settingKey].integer}
                            />
                          )}
                      </div>
                    </div>
                  );
                })}
              <img src={advanceSettingsDivider} alt="divider"  className="advance-settings-divider" />
              </div>
            )}
            {lifetime && (
              <div>
                <div className="subheading__title">
                  {" "}
                  {t("advanceSettings.lifetime")}
                </div>
                {Object.keys(settingsConfigData.lifetime).map((settingKey) => (
                  <div className="flex-between-center" key={settingKey}>
                    <label className="font-14">
                      {settingsConfigData.lifetime[settingKey].label}
                    </label>
                    <div
                      className={`dropdown-width flex-gap-12  marginBtm10${(suggestedSettings[settingKey] === null || suggestedSettings[settingKey] === undefined) ? ' dropdown-width-single' : ''}`}
                    
                    >
                      <WPRangedInput
                        value={
                          settings[settingKey] === undefined
                            ? defaultSettings[settingKey]
                            : settings[settingKey]
                        }
                        onChange={e =>
                          handleChange(
                            settingKey,
                            e && e.target ? e.target.value : e,
                            settingsConfigData.lifetime[settingKey].ranges?.hard?.min,
                            settingsConfigData.lifetime[settingKey].ranges?.hard?.max
                          )
                        }
                        ranges={settingsConfigData.lifetime[settingKey].ranges}
                        errorMsg={errors[settingKey]}
                        isError={!!errors[settingKey]}
                        maxLength={10}
                      />
                      {suggestedSettings[settingKey] !== null &&suggestedSettings[settingKey] !== undefined&&
                        (
                          <WPRangedInput
                            value={suggestedSettings[settingKey]}
                            disabled={true}
                            ranges={settingsConfigData.lifetime[settingKey].ranges}
                            maxLength={10}
                            integer={settingsConfigData.lifetime[settingKey].integer}
                          />
                        )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </CustomModal>
    </>
  );
};

export default AdvanceSettings;
